import i18next from 'i18next';
import { supportedLngs } from 'src/i18n';
import { setSentryUser } from 'src/sentry';
import { useState, useCallback, createContext } from 'react';

import type { DoulaUser, UserProfile } from 'src/types/api';

export interface UserContextValue {
  lang: string;
  setLang: (lang?: string) => void;
  user: DoulaUser | undefined;
  setUser: (profile?: UserProfile) => void;
  hasAlerts: boolean;
  setHasAlerts: React.Dispatch<React.SetStateAction<boolean>>;
}

const UserContext = createContext<UserContextValue>({} as UserContextValue);

const roleAccess = ['patient', 'app'];

type Props = {
  children?: React.ReactNode;
};

export const UserProvider = ({ children }: Props) => {
  const [lang, setLang] = useState(i18next.language);
  const [user, setUser] = useState<DoulaUser>();
  const [hasAlerts, setHasAlerts] = useState(false);
  // const cookies = new Cookies();

  const processUser = useCallback((passedUser?: UserProfile) => {
    setSentryUser(passedUser);
    if (!passedUser) {
      setUser(undefined);
      return;
    }
    // if there was a cached language, don't update with language from profile
    // if (!cookies.get('language-cached'))
    modifyLanguage(passedUser.language);

    const displayName = [passedUser.first_name, passedUser.last_name].filter(Boolean).join(' ');
    setUser({
      ...passedUser,
      ...(displayName && { displayName }),
      access: roleAccess,
    });
  }, []);

  const modifyLanguage = (languageInput?: string) => {
    if (languageInput) {
      let language: string | string[] = languageInput.split('-');
      language = language.length > 1 ? `${language[0]}-${language[1].toUpperCase()}` : language[0];
      if (supportedLngs.includes(language)) {
        i18next.changeLanguage(language);
        setLang(language);
      }
    }
  };

  return (
    <UserContext.Provider
      value={{
        lang,
        setLang: modifyLanguage,
        user,
        setUser: processUser,
        hasAlerts,
        setHasAlerts,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
